import type { PullRequestDetail } from "@cmmd-forge/contracts";
import {
  BookOpenIcon,
  FolderGit2Icon,
  GitBranchIcon,
  MessageCircleQuestionIcon,
  ServerIcon,
  TriangleAlertIcon,
  WrenchIcon,
} from "lucide-react";

import type { PullRequestHandoffAction } from "~/pullRequest/handoff/pullRequestHandoffPlanning";
import {
  usePullRequestHandoffController,
  type HandoffController,
} from "~/pullRequest/handoff/PullRequestHandoffProvider";
import {
  pullRequestHandoffProjectKey as projectKey,
  pullRequestHandoffTargetLabel,
} from "~/pullRequest/handoff/pullRequestHandoffEnvironmentLabels";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "~/components/ui/menu";
import type { PullRequestAgentSelection } from "~/pullRequest/pullRequestSelectionContext";

export function PullRequestCheckoutAction() {
  const handoff = usePullRequestHandoffController();
  if (handoff === null || !handoff.canPickTarget) return null;
  const pending = handoff.pendingAction !== null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button type="button" size="xs" variant="outline" disabled={pending} />}
      >
        <GitBranchIcon aria-hidden className="size-3.5" />
        {pending && handoff.pendingAction?.startsWith("checkout") ? "Checking out…" : "Check out"}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-72">
        <DropdownMenuItem onClick={() => void handoff.run("checkout-worktree")}>
          <GitBranchIcon className="mt-0.5 size-3.5 shrink-0 self-start" />
          <span className="flex min-w-0 flex-col">
            <span>In a separate worktree</span>
            <span className="text-xs text-muted-foreground">
              Its own folder and thread. Nothing you have open moves.
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void handoff.run("checkout-local")}>
          <FolderGit2Icon className="mt-0.5 size-3.5 shrink-0 self-start" />
          <span className="flex min-w-0 flex-col">
            <span>In this repository</span>
            <span className="text-xs text-muted-foreground">
              Switches the branch you are working in, like `gh pr checkout`.
            </span>
          </span>
        </DropdownMenuItem>
        <ActOnEnvironmentPicker handoff={handoff} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PullRequestHandoffMenuItems(props: {
  readonly detail: Pick<PullRequestDetail, "mergeability" | "state">;
  readonly selection: PullRequestAgentSelection | null;
}) {
  const handoff = usePullRequestHandoffController();
  if (handoff === null) return null;
  const run = (action: PullRequestHandoffAction) => void handoff.run(action);
  return (
    <>
      <DropdownMenuItem disabled={handoff.pendingAction !== null} onClick={() => run("ask")}>
        <MessageCircleQuestionIcon className="mt-0.5 size-3.5 shrink-0 self-start" />
        <span className="flex min-w-0 flex-col">
          <span>{handoff.pendingAction === "ask" ? "Opening…" : "Ask a question"}</span>
          <span className="text-xs text-muted-foreground">
            {handoff.currentThreadMatches
              ? "Adds the pull request to this thread's composer."
              : "Opens a thread that knows which pull request you mean."}
          </span>
        </span>
      </DropdownMenuItem>
      <DropdownMenuItem disabled={handoff.pendingAction !== null} onClick={() => run("explain")}>
        <BookOpenIcon className="mt-0.5 size-3.5 shrink-0 self-start" />
        <span className="flex min-w-0 flex-col">
          <span>{handoff.pendingAction === "explain" ? "Opening…" : "Explain this PR"}</span>
          <span className="text-xs text-muted-foreground">
            A walk through the diff and what to read closely.
          </span>
        </span>
      </DropdownMenuItem>
      <SelectedCodeMenuItem selection={props.selection} onSelect={() => run("selection")} />
      <DropdownMenuItem disabled={handoff.pendingAction !== null} onClick={() => run("fix")}>
        <WrenchIcon /> {handoff.currentThreadMatches ? "Fix in this thread" : "Fix in a thread"}
      </DropdownMenuItem>
      {props.detail.state === "open" && props.detail.mergeability === "conflicting" ? (
        <DropdownMenuItem
          disabled={handoff.pendingAction !== null}
          onClick={() => run("resolve-conflicts")}
        >
          <TriangleAlertIcon /> Resolve conflicts
        </DropdownMenuItem>
      ) : null}
      <ActOnEnvironmentPicker handoff={handoff} />
      {projectKey(handoff.targetProject) === projectKey(handoff.sourceProject) ? null : (
        <DropdownMenuItem onClick={() => run("transfer")}>
          <ServerIcon /> Continue conversation in this environment
        </DropdownMenuItem>
      )}
    </>
  );
}

function SelectedCodeMenuItem(props: {
  readonly selection: PullRequestAgentSelection | null;
  readonly onSelect: () => void;
}) {
  if (props.selection === null) return null;
  return <DropdownMenuItem onClick={props.onSelect}>Add selected code</DropdownMenuItem>;
}

/**
 * Which checkout the handoff will act on.
 *
 * A radio group rather than a list of commands: the pull request cannot say which checkout the
 * reader meant, so the one that will be used is shown as chosen instead of being left to a
 * silent default. Disabling the active row, as this did before, hid exactly that.
 */
export function ActOnEnvironmentPicker(props: { readonly handoff: HandoffController }) {
  const targets = props.handoff.targets;
  if (!props.handoff.canPickTarget || targets.length < 2) return null;
  // A handoff already in flight is preparing a checkout; re-pointing it midway would act on one
  // checkout and land in another.
  const pending = props.handoff.pendingAction !== null;
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>Act in environment</DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup
          value={projectKey(props.handoff.targetProject)}
          onValueChange={(next) => {
            const chosen = targets.find((project) => projectKey(project) === next);
            if (chosen !== undefined) props.handoff.selectTarget(chosen);
          }}
        >
          {targets.map((project) => (
            <DropdownMenuRadioItem
              key={projectKey(project)}
              value={projectKey(project)}
              disabled={pending}
            >
              {pullRequestHandoffTargetLabel(project, props.handoff.environmentLabels)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
